import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

const base = 'performance';

const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '-';
    }

    return Number(value).toLocaleString('en-MY', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

const formatCurrency = (value, decimals = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '-';
    }

    return `RM ${formatNumber(value, decimals)}`;
};

const formatDifference = (value, formatter = formatNumber) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '-';
    }

    const number = Number(value);
    if (number <= 0) {
        return `+${formatter(Math.abs(number))}`;
    }

    return formatter(number);
};

const formatMinutes = (value) => {
    if (!value || isNaN(value)) {
        return '-';
    }

    return `${Math.round(value)} min`;
};

const SERVICE_SPEED_MUST_HAVE_TARGET_MINS = 5;
const SERVICE_SPEED_WINNING_TARGET_MINS = 4;
const SERVICE_SPEED_HERO_TARGET_MINS = 3;

const getHigherIsBetterStatus = (actual, mustHaveTarget, winningTarget, heroTarget) => {
    if (actual === null || actual === undefined || isNaN(actual)) {
        return '';
    }

    const value = Number(actual);
    if (heroTarget !== null && heroTarget !== undefined && !isNaN(heroTarget) && value >= Number(heroTarget)) {
        return 'hero';
    }
    if (winningTarget !== null && winningTarget !== undefined && !isNaN(winningTarget) && value >= Number(winningTarget)) {
        return 'good';
    }
    if (mustHaveTarget !== null && mustHaveTarget !== undefined && !isNaN(mustHaveTarget) && value >= Number(mustHaveTarget)) {
        return 'watch';
    }
    if (mustHaveTarget !== null && mustHaveTarget !== undefined && !isNaN(mustHaveTarget)) {
        return 'bad';
    }

    return '';
};

const getLowerIsBetterStatus = (actual, mustHaveTarget, winningTarget, heroTarget) => {
    if (actual === null || actual === undefined || isNaN(actual)) {
        return '';
    }

    const value = Number(actual);
    if (heroTarget !== null && heroTarget !== undefined && !isNaN(heroTarget) && value <= Number(heroTarget)) {
        return 'hero';
    }
    if (winningTarget !== null && winningTarget !== undefined && !isNaN(winningTarget) && value <= Number(winningTarget)) {
        return 'good';
    }
    if (mustHaveTarget !== null && mustHaveTarget !== undefined && !isNaN(mustHaveTarget) && value <= Number(mustHaveTarget)) {
        return 'watch';
    }
    if (mustHaveTarget !== null && mustHaveTarget !== undefined && !isNaN(mustHaveTarget)) {
        return 'bad';
    }

    return '';
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'hero':
            return 'Hero';
        case 'good':
            return 'On track';
        case 'watch':
            return 'Watch';
        case 'bad':
            return 'Below target';
        default:
            return '';
    }
};

@inject('HubPerformanceStore')
@observer
class HubPerformanceData extends Component {
    getMetricRows = () => {
        const { HubPerformanceStore } = this.props;
        const {
            todayOrders,
            yesterdayOrders,
            monthToDateOrders,
            todayTarget,
            yesterdayTarget,
            criticalComponentsOutOfStock,
            getRevenue,
            getTransactions,
            getAverageBasket,
            getCancelledOrders,
            getAverageServiceSpeedMinutes,
            getMonthToDateTargetRows,
            sumTargetRows,
        } = HubPerformanceStore;

        const todayRevenue = getRevenue(todayOrders);
        const todayTransactions = getTransactions(todayOrders);
        const todayAverageBasket = getAverageBasket(todayOrders);
        const todayServiceSpeed = getAverageServiceSpeedMinutes(todayOrders);
        const todayCancelledOrders = getCancelledOrders(todayOrders);

        const yesterdayRevenue = getRevenue(yesterdayOrders);
        const yesterdayTransactions = getTransactions(yesterdayOrders);
        const yesterdayAverageBasket = getAverageBasket(yesterdayOrders);
        const yesterdayServiceSpeed = getAverageServiceSpeedMinutes(yesterdayOrders);
        const yesterdayCancelledOrders = getCancelledOrders(yesterdayOrders);

        const monthToDateRevenue = getRevenue(monthToDateOrders);
        const monthToDateTransactions = getTransactions(monthToDateOrders);
        const monthToDateAverageBasket = getAverageBasket(monthToDateOrders);
        const monthToDateServiceSpeed = getAverageServiceSpeedMinutes(monthToDateOrders);
        const monthToDateCancelledOrders = getCancelledOrders(monthToDateOrders);
        const monthToDateTargetRows = getMonthToDateTargetRows();
        const monthToDateMustHaveRevenueTarget = sumTargetRows(monthToDateTargetRows, 'Must Have Daily Revenue Target Rm');
        const monthToDateWinningRevenueTarget = sumTargetRows(monthToDateTargetRows, 'Winning Daily Revenue Target Rm');
        const monthToDateHeroRevenueTarget = sumTargetRows(monthToDateTargetRows, 'Hero Daily Revenue Target Rm');
        const monthToDateMustHaveTransactionTarget = sumTargetRows(monthToDateTargetRows, 'Must Have Daily Transaction Target');
        const monthToDateWinningTransactionTarget = sumTargetRows(monthToDateTargetRows, 'Winning Daily Transaction Target');
        const monthToDateHeroTransactionTarget = sumTargetRows(monthToDateTargetRows, 'Hero Daily Transaction Target');
        const monthToDateMustHaveAovTarget = monthToDateMustHaveTransactionTarget
            ? monthToDateMustHaveRevenueTarget / monthToDateMustHaveTransactionTarget
            : null;
        const monthToDateWinningAovTarget = monthToDateWinningTransactionTarget
            ? monthToDateWinningRevenueTarget / monthToDateWinningTransactionTarget
            : null;
        const monthToDateHeroAovTarget = monthToDateHeroTransactionTarget
            ? monthToDateHeroRevenueTarget / monthToDateHeroTransactionTarget
            : null;

        return {
            today: [
                {
                    metric: 'Sales Today',
                    today: formatCurrency(todayRevenue),
                    mustHaveTarget: formatCurrency(todayTarget && todayTarget['Must Have Daily Revenue Target Rm']),
                    winningTarget: formatCurrency(todayTarget && todayTarget['Winning Daily Revenue Target Rm']),
                    heroTarget: formatCurrency(todayTarget && todayTarget['Hero Daily Revenue Target Rm']),
                    difference: formatDifference((todayTarget && todayTarget['Winning Daily Revenue Target Rm']) - todayRevenue, formatCurrency),
                    status: getHigherIsBetterStatus(todayRevenue, todayTarget && todayTarget['Must Have Daily Revenue Target Rm'], todayTarget && todayTarget['Winning Daily Revenue Target Rm'], todayTarget && todayTarget['Hero Daily Revenue Target Rm']),
                    action: '',
                },
                {
                    metric: 'Transactions',
                    today: formatNumber(todayTransactions),
                    mustHaveTarget: formatNumber(todayTarget && todayTarget['Must Have Daily Transaction Target']),
                    winningTarget: formatNumber(todayTarget && todayTarget['Winning Daily Transaction Target']),
                    heroTarget: formatNumber(todayTarget && todayTarget['Hero Daily Transaction Target']),
                    difference: formatDifference((todayTarget && todayTarget['Winning Daily Transaction Target']) - todayTransactions, formatNumber),
                    status: getHigherIsBetterStatus(todayTransactions, todayTarget && todayTarget['Must Have Daily Transaction Target'], todayTarget && todayTarget['Winning Daily Transaction Target'], todayTarget && todayTarget['Hero Daily Transaction Target']),
                    action: 'Approach passerbys, Sampling',
                },
                {
                    metric: 'Average Basket',
                    today: formatCurrency(todayAverageBasket, 1),
                    mustHaveTarget: formatCurrency(todayTarget && todayTarget['Must Have Daily Aov Target Rm'], 1),
                    winningTarget: formatCurrency(todayTarget && todayTarget['Winning Daily Aov Target Rm'], 1),
                    heroTarget: formatCurrency(todayTarget && todayTarget['Hero Daily Aov Target Rm'], 1),
                    difference: formatDifference((todayTarget && todayTarget['Winning Daily Aov Target Rm']) - todayAverageBasket, (value) => formatCurrency(value, 1)),
                    status: getHigherIsBetterStatus(todayAverageBasket, todayTarget && todayTarget['Must Have Daily Aov Target Rm'], todayTarget && todayTarget['Winning Daily Aov Target Rm'], todayTarget && todayTarget['Hero Daily Aov Target Rm']),
                    action: 'Recommend Drinks and Desserts',
                },
                {
                    metric: 'Cancelled Orders',
                    today: formatNumber(todayCancelledOrders),
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: todayCancelledOrders === 0 ? 'good' : 'bad',
                    action: '',
                },
                {
                    metric: 'Critical Components out of Stock',
                    today: criticalComponentsOutOfStock.length ? criticalComponentsOutOfStock.join(', ') : 'None',
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: criticalComponentsOutOfStock.length ? 'bad' : 'good',
                    action: criticalComponentsOutOfStock.length ? 'Inter Outlet Transfer' : '',
                },
                {
                    metric: 'Service Speed Today',
                    today: formatMinutes(todayServiceSpeed),
                    mustHaveTarget: formatMinutes(SERVICE_SPEED_MUST_HAVE_TARGET_MINS),
                    winningTarget: formatMinutes(SERVICE_SPEED_WINNING_TARGET_MINS),
                    heroTarget: formatMinutes(SERVICE_SPEED_HERO_TARGET_MINS),
                    difference: todayServiceSpeed ? formatMinutes(todayServiceSpeed - SERVICE_SPEED_WINNING_TARGET_MINS) : '-',
                    status: getLowerIsBetterStatus(
                        todayServiceSpeed,
                        SERVICE_SPEED_MUST_HAVE_TARGET_MINS,
                        SERVICE_SPEED_WINNING_TARGET_MINS,
                        SERVICE_SPEED_HERO_TARGET_MINS
                    ),
                    action: 'Clear stations',
                },
            ],
            yesterday: [
                {
                    metric: 'Sales Yesterday',
                    today: formatCurrency(yesterdayRevenue),
                    mustHaveTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Must Have Daily Revenue Target Rm']),
                    winningTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Winning Daily Revenue Target Rm']),
                    heroTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Hero Daily Revenue Target Rm']),
                    difference: formatDifference((yesterdayTarget && yesterdayTarget['Winning Daily Revenue Target Rm']) - yesterdayRevenue, formatCurrency),
                    status: getHigherIsBetterStatus(yesterdayRevenue, yesterdayTarget && yesterdayTarget['Must Have Daily Revenue Target Rm'], yesterdayTarget && yesterdayTarget['Winning Daily Revenue Target Rm'], yesterdayTarget && yesterdayTarget['Hero Daily Revenue Target Rm']),
                    action: '',
                },
                {
                    metric: 'Transactions',
                    today: formatNumber(yesterdayTransactions),
                    mustHaveTarget: formatNumber(yesterdayTarget && yesterdayTarget['Must Have Daily Transaction Target']),
                    winningTarget: formatNumber(yesterdayTarget && yesterdayTarget['Winning Daily Transaction Target']),
                    heroTarget: formatNumber(yesterdayTarget && yesterdayTarget['Hero Daily Transaction Target']),
                    difference: formatDifference((yesterdayTarget && yesterdayTarget['Winning Daily Transaction Target']) - yesterdayTransactions, formatNumber),
                    status: getHigherIsBetterStatus(yesterdayTransactions, yesterdayTarget && yesterdayTarget['Must Have Daily Transaction Target'], yesterdayTarget && yesterdayTarget['Winning Daily Transaction Target'], yesterdayTarget && yesterdayTarget['Hero Daily Transaction Target']),
                    action: 'Approach passerbys, Sampling',
                },
                {
                    metric: 'Average Basket',
                    today: formatCurrency(yesterdayAverageBasket, 1),
                    mustHaveTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Must Have Daily Aov Target Rm'], 1),
                    winningTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Winning Daily Aov Target Rm'], 1),
                    heroTarget: formatCurrency(yesterdayTarget && yesterdayTarget['Hero Daily Aov Target Rm'], 1),
                    difference: formatDifference((yesterdayTarget && yesterdayTarget['Winning Daily Aov Target Rm']) - yesterdayAverageBasket, (value) => formatCurrency(value, 1)),
                    status: getHigherIsBetterStatus(yesterdayAverageBasket, yesterdayTarget && yesterdayTarget['Must Have Daily Aov Target Rm'], yesterdayTarget && yesterdayTarget['Winning Daily Aov Target Rm'], yesterdayTarget && yesterdayTarget['Hero Daily Aov Target Rm']),
                    action: 'Recommend Drinks and Desserts',
                },
                {
                    metric: 'Cancelled Orders',
                    today: formatNumber(yesterdayCancelledOrders),
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: yesterdayCancelledOrders === 0 ? 'good' : 'bad',
                    action: '',
                },
                {
                    metric: 'Bestseller Availability',
                    today: '-',
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: '',
                    action: 'Inter Outlet Transfer',
                },
                {
                    metric: 'Service Speed Yesterday',
                    today: formatMinutes(yesterdayServiceSpeed),
                    mustHaveTarget: formatMinutes(SERVICE_SPEED_MUST_HAVE_TARGET_MINS),
                    winningTarget: formatMinutes(SERVICE_SPEED_WINNING_TARGET_MINS),
                    heroTarget: formatMinutes(SERVICE_SPEED_HERO_TARGET_MINS),
                    difference: yesterdayServiceSpeed ? formatMinutes(yesterdayServiceSpeed - SERVICE_SPEED_WINNING_TARGET_MINS) : '-',
                    status: getLowerIsBetterStatus(
                        yesterdayServiceSpeed,
                        SERVICE_SPEED_MUST_HAVE_TARGET_MINS,
                        SERVICE_SPEED_WINNING_TARGET_MINS,
                        SERVICE_SPEED_HERO_TARGET_MINS
                    ),
                    action: 'Clear stations',
                },
            ],
            monthToDate: [
                {
                    metric: 'Sales Month to Date',
                    today: formatCurrency(monthToDateRevenue),
                    mustHaveTarget: formatCurrency(monthToDateMustHaveRevenueTarget),
                    winningTarget: formatCurrency(monthToDateWinningRevenueTarget),
                    heroTarget: formatCurrency(monthToDateHeroRevenueTarget),
                    difference: formatDifference(monthToDateWinningRevenueTarget - monthToDateRevenue, formatCurrency),
                    status: getHigherIsBetterStatus(monthToDateRevenue, monthToDateMustHaveRevenueTarget, monthToDateWinningRevenueTarget, monthToDateHeroRevenueTarget),
                    action: '',
                },
                {
                    metric: 'Transactions',
                    today: formatNumber(monthToDateTransactions),
                    mustHaveTarget: formatNumber(monthToDateMustHaveTransactionTarget),
                    winningTarget: formatNumber(monthToDateWinningTransactionTarget),
                    heroTarget: formatNumber(monthToDateHeroTransactionTarget),
                    difference: formatDifference(monthToDateWinningTransactionTarget - monthToDateTransactions, formatNumber),
                    status: getHigherIsBetterStatus(monthToDateTransactions, monthToDateMustHaveTransactionTarget, monthToDateWinningTransactionTarget, monthToDateHeroTransactionTarget),
                    action: 'Approach passerbys, Sampling',
                },
                {
                    metric: 'Average Basket',
                    today: formatCurrency(monthToDateAverageBasket, 1),
                    mustHaveTarget: formatCurrency(monthToDateMustHaveAovTarget, 1),
                    winningTarget: formatCurrency(monthToDateWinningAovTarget, 1),
                    heroTarget: formatCurrency(monthToDateHeroAovTarget, 1),
                    difference: formatDifference(monthToDateWinningAovTarget - monthToDateAverageBasket, (value) => formatCurrency(value, 1)),
                    status: getHigherIsBetterStatus(monthToDateAverageBasket, monthToDateMustHaveAovTarget, monthToDateWinningAovTarget, monthToDateHeroAovTarget),
                    action: 'Recommend Drinks and Desserts',
                },
                {
                    metric: 'Cancelled Orders',
                    today: formatNumber(monthToDateCancelledOrders),
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: monthToDateCancelledOrders === 0 ? 'good' : 'bad',
                    action: '',
                },
                {
                    metric: 'Critical Components out of Stock',
                    today: criticalComponentsOutOfStock.length ? criticalComponentsOutOfStock.join(', ') : 'None',
                    mustHaveTarget: '',
                    winningTarget: '',
                    heroTarget: '',
                    difference: '',
                    status: criticalComponentsOutOfStock.length ? 'bad' : 'good',
                    action: criticalComponentsOutOfStock.length ? 'Inter Outlet Transfer' : '',
                },
                {
                    metric: 'Service Speed Month to Date',
                    today: formatMinutes(monthToDateServiceSpeed),
                    mustHaveTarget: formatMinutes(SERVICE_SPEED_MUST_HAVE_TARGET_MINS),
                    winningTarget: formatMinutes(SERVICE_SPEED_WINNING_TARGET_MINS),
                    heroTarget: formatMinutes(SERVICE_SPEED_HERO_TARGET_MINS),
                    difference: monthToDateServiceSpeed ? formatMinutes(monthToDateServiceSpeed - SERVICE_SPEED_WINNING_TARGET_MINS) : '-',
                    status: getLowerIsBetterStatus(
                        monthToDateServiceSpeed,
                        SERVICE_SPEED_MUST_HAVE_TARGET_MINS,
                        SERVICE_SPEED_WINNING_TARGET_MINS,
                        SERVICE_SPEED_HERO_TARGET_MINS
                    ),
                    action: 'Clear stations',
                },
            ],
        };
    };

    renderPerformanceTable = (rows, valueLabel) => (
        <div className={`${base}__table-scroll`}>
            <table className={`${base}__table ${base}__tracker-table`}>
                <thead>
                    <tr>
                        <th>Metric</th>
                        <th>{valueLabel}</th>
                        <th>Must Have Target</th>
                        <th>Winning Target</th>
                        <th>Hero Target</th>
                        <th>Difference to Winning Target</th>
                        <th>What to do?</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.metric} className={row.status ? `${base}__status-row ${base}__status-row--${row.status}` : ''}>
                            <th>{row.metric}</th>
                            <td>
                                <span className={`${base}__value`}>{row.today}</span>
                                {row.status && (
                                    <span className={`${base}__status-pill ${base}__status-pill--${row.status}`}>
                                        {getStatusLabel(row.status)}
                                    </span>
                                )}
                            </td>
                            <td>{row.mustHaveTarget}</td>
                            <td>{row.winningTarget}</td>
                            <td>{row.heroTarget}</td>
                            <td className={row.status ? `${base}__difference ${base}__difference--${row.status}` : `${base}__difference`}>{row.difference}</td>
                            <td>{row.action}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    render() {
        const { today, yesterday, monthToDate } = this.getMetricRows();

        return (
            <div className={`${base}`}>
                <section className={`${base}__section`}>
                    <h1 className={`${base}__section-title`}>Today</h1>
                    {this.renderPerformanceTable(today, 'Today')}
                </section>
                <section className={`${base}__section`}>
                    <h1 className={`${base}__section-title`}>Yesterday</h1>
                    {this.renderPerformanceTable(yesterday, 'Yesterday')}
                </section>
                <section className={`${base}__section`}>
                    <h1 className={`${base}__section-title`}>Month to Date</h1>
                    {this.renderPerformanceTable(monthToDate, 'Month to Date')}
                </section>
            </div>
        );
    }
}

export default HubPerformanceData;
